import { Link, MessageBar, MessageBarType } from '@fluentui/react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocation } from 'react-router';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { routeTable } from 'src/core/utils/routeTable';

const messages = defineMessages({
    noticeMessage: {
        id: 'global.moveToFoundry',
        defaultMessage:
            'Try {==} in Azure AI Foundry—fine-tune your models faster with new features, agent integration, and a unified experience with all of Azure AI. See {===} for more information on configuring your resources in Azure AI Foundry.'
    },
    and: {
        id: 'global.and',
        defaultMessage: 'and'
    },
    cqa: {
        id: 'global.CQA',
        defaultMessage: 'CQA'
    },
    clu: {
        id: 'global.CLU',
        defaultMessage: 'CLU'
    },
    prebuiltFeatures: {
        id: 'global.prebuiltMoveToFoundry',
        defaultMessage: 'Try {==} in Azure AI Playgrounds. See {===} for more information on Azure AI Foundry.'
    },
    summarization: {
        id: 'global.summarization',
        defaultMessage: 'summarization'
    },
    documentTranslation: {
        id: 'global.documentTranslation',
        defaultMessage: 'Document translation'
    },
    extraPii: {
        id: 'global.extraPii',
        defaultMessage: 'Personally identifiable information detection'
    },
    extractKeyPhrases: {
        id: 'global.extractKeyPhrases',
        defaultMessage: 'Key phrase extraction'
    },
    extractNamedEntities: {
        id: 'global.extractNamedEntities',
        defaultMessage: 'Named entity extraction'
    },
    extractHealth: {
        id: 'global.extractHealth',
        defaultMessage: 'Health information extraction'
    },
    analyzeSentiment: {
        id: 'global.analyzeSentiment',
        defaultMessage: 'Sentiment analysis and opinion mining'
    },
    detectLanguage: {
        id: 'global.detectLanguage',
        defaultMessage: 'Language detection'
    }
});
export const FeatureMovement = () => {
    const { localize } = useLocalization();
    const { pathname } = useLocation();

    const isHomePage = pathname === routeTable.languageStudio.home();
    // fine tune
    const isClu = pathname.includes(routeTable.clu.root());
    const isCqa = pathname.includes(routeTable.qnaMaker.root());
    // prebuilt
    const isExtraPii = pathname.includes(routeTable.languageStudio.piiDemoPage());
    const isExtractKeyPhrases = pathname.includes(routeTable.languageStudio.keyPhrasesDemoPage());
    const isAnalyzeSentiment = pathname.includes(routeTable.languageStudio.sentimentDemoPage());
    const isExtractNamedEntity = pathname.includes(routeTable.languageStudio.namedEntitiesDemoPage());
    const isDetectLanguage = pathname.includes(routeTable.languageStudio.detectLanguageDemoPage());
    const isSummarization = pathname.includes(routeTable.languageStudio.summarizationDemoPage());
    const isTA4H = pathname.includes(routeTable.languageStudio.taForHealthDemoPage());
    const isTranslateText = pathname.includes(routeTable.languageStudio.textTranslationDemoPage());
    // const isDocumentTranslation =
    //     pathname.includes(routeTable.languageStudio.documentTranslationDemoPage()) || pathname.includes(routeTable.translation.root());

    const isFineTune = isClu || isCqa;
    const isPrebuilt =
        isExtraPii ||
        isExtractKeyPhrases ||
        isAnalyzeSentiment ||
        isDetectLanguage ||
        isExtractNamedEntity ||
        isSummarization ||
        isTA4H ||
        isTranslateText;

    const renderMessage = React.useCallback(() => {
        let fullMessage = isPrebuilt ? localize(messages.prebuiltFeatures) : localize(messages.noticeMessage);
        let ele = <></>;
        const getFeatureName = (key: string) => localize(messages[key as keyof typeof messages]);
        const clu = localize(messages.clu);
        const cqa = localize(messages.cqa);
        const and = localize(messages.and);

        if (isHomePage) {
            fullMessage = fullMessage.replace('{==}', `${clu} ${and} ${cqa}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/conversational-language-understanding/how-to/configure-azure-resources"
                        target="_blank"
                        underline>
                        {localize(messages.clu)}
                    </Link>{' '}
                    {localize(messages.and)}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/question-answering/how-to/configure-azure-resources"
                        target="_blank"
                        underline>
                        {localize(messages.cqa)}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }
        if (isClu) {
            fullMessage = fullMessage.replace('{==}', `${clu}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/conversational-language-understanding/how-to/configure-azure-resources"
                        target="_blank"
                        underline>
                        {localize(messages.clu)}
                    </Link>{' '}
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }
        if (isCqa) {
            fullMessage = fullMessage.replace('{==}', `${cqa}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/question-answering/how-to/configure-azure-resources"
                        target="_blank"
                        underline>
                        {localize(messages.cqa)}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }
        if (isSummarization) {
            fullMessage = fullMessage.replace('{==}', `${getFeatureName('summarization')}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/summarization/quickstart?tabs=text-summarization%2Cwindows&pivots=ai-foundry-portal"
                        target="_blank"
                        underline>
                        {getFeatureName('summarization')}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }
        // if (isDocumentTranslation) {
        //     fullMessage = fullMessage.replace('{==}', `${getFeatureName('documentTranslation')}`);
        //     ele = (
        //         <>
        //             {fullMessage.split('{===}')[0]}{' '}
        //             <Link
        //                 href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/summarization/quickstart?tabs=text-summarization%2Cwindows&pivots=ai-foundry-portal"
        //                 target="_blank"
        //                 underline>
        //                 {getFeatureName('documentTranslation')}
        //             </Link>
        //             {fullMessage.split('{===}')[1]}
        //         </>
        //     );
        // }
        if (isExtraPii) {
            fullMessage = fullMessage.replace('{==}', `${getFeatureName('extraPii')}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/personally-identifiable-information/quickstart?tabs=windows&pivots=ai-foundry-portal"
                        target="_blank"
                        underline>
                        {getFeatureName('extraPii')}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }

        if (isExtractKeyPhrases) {
            fullMessage = fullMessage.replace('{==}', `${getFeatureName('extractKeyPhrases')}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/key-phrase-extraction/quickstart?tabs=windows&pivots=ai-foundry-portal"
                        target="_blank"
                        underline>
                        {getFeatureName('extractKeyPhrases')}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }
        if (isExtractNamedEntity) {
            fullMessage = fullMessage.replace('{==}', `${getFeatureName('extractNamedEntities')}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/named-entity-recognition/quickstart?tabs=windows%2Cga-api&pivots=ai-foundry-portal"
                        target="_blank"
                        underline>
                        {getFeatureName('extractNamedEntities')}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }

        if (isTA4H) {
            fullMessage = fullMessage.replace('{==}', `${getFeatureName('extractHealth')}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/text-analytics-for-health/quickstart?tabs=windows&pivots=ai-foundry-portal"
                        target="_blank"
                        underline>
                        {getFeatureName('extractHealth')}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }

        if (isAnalyzeSentiment) {
            fullMessage = fullMessage.replace('{==}', `${getFeatureName('analyzeSentiment')}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/sentiment-opinion-mining/quickstart?tabs=windows&pivots=ai-foundry-portal"
                        target="_blank"
                        underline>
                        {getFeatureName('analyzeSentiment')}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }
        if (isDetectLanguage) {
            fullMessage = fullMessage.replace('{==}', `${getFeatureName('detectLanguage')}`);
            ele = (
                <>
                    {fullMessage.split('{===}')[0]}{' '}
                    <Link
                        href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/language-detection/quickstart?tabs=windows&pivots=ai-foundry-portal"
                        target="_blank"
                        underline>
                        {getFeatureName('detectLanguage')}
                    </Link>
                    {fullMessage.split('{===}')[1]}
                </>
            );
        }

        return ele;
    }, [messages, isHomePage, isPrebuilt, isFineTune]);

    return isHomePage || isFineTune || isPrebuilt ? (
        <MessageBar id="warningMessageBar" messageBarType={MessageBarType.warning} isMultiline={false} dismissButtonAriaLabel="Close">
            {renderMessage()}
        </MessageBar>
    ) : null;
};
